import * as React from 'react';
import { observer, Observer } from 'mobx-react';
import { Page, Pdf, Outline, Document } from 'react-pdf';
import _ from 'lodash';
import { AutoSizer } from 'react-virtualized';
import { findDOMNode } from 'react-dom';
import { DocumentViewVisualStore } from '../stores';
import DocumentPagination from '../../common/components/DocumentPagination';

type State = {
    numPages: number;
    pageNumber: number;
};

type Props = {
    store: DocumentViewVisualStore;
};

const setPages = (numPages: number, prevState: State) => ({ ...prevState, numPages: numPages });

export class PdfViewer extends React.Component<Props, State> {
    readonly state: State = {
        numPages: 0,
        pageNumber: 1
    };

    pageRefs: { [key: number]: Page } = {};
    scrollContainerRef: HTMLElement | null = null;

    handleScroll = _.throttle(() => {
        if (!this.scrollContainerRef) {
            return;
        }

        const scrollTop = this.scrollContainerRef.scrollTop;
        const viewportHeight = this.scrollContainerRef.clientHeight;
        const midpoint = scrollTop + viewportHeight / 2;

        for (let i = 1; i <= this.state.numPages; i++) {
            const pageElement = findDOMNode(this.pageRefs[i]) as HTMLElement;
            if (pageElement) {
                const pageTop = pageElement.offsetTop;
                const pageBottom = pageTop + pageElement.offsetHeight;

                if (midpoint >= pageTop && midpoint <= pageBottom) {
                    if (this.state.pageNumber !== i) {
                        this.setState({ pageNumber: i });
                    }
                    break;
                }
            }
        }
    }, 100);

    constructor(props: Props) {
        super(props);
        this.handleDocumentLoaded = this.handleDocumentLoaded.bind(this);
    }

    componentWillUnmount() {
        this.handleScroll.cancel();
        if (this.scrollContainerRef) {
            this.scrollContainerRef.removeEventListener('scroll', this.handleScroll);
        }
    }

    navigateToPage(pageNumber: number) {
        const page = findDOMNode(this.pageRefs[pageNumber]) as HTMLElement;
        if (page) {
            page.parentElement!.parentElement!.scrollTo(0, page.offsetTop);
            this.setState({ pageNumber });
        }
    }

    render() {
        const { store } = this.props;
        const { pageNumber, numPages } = this.state;
        return (
            <div className="document-viewer-container">
                <div className="document-viewer-info-header">Document name: {store.file?.name || 'N/A'}</div>
                <DocumentPagination
                    currentPage={pageNumber}
                    totalPages={numPages}
                    onPageChange={this.navigateToPage.bind(this)}
                    scale={store.scale}
                    onScaleChange={scale => store.setScale(scale * 100)}
                    className="pdf-viewer-pagination"
                />
                <Document file={this.props.store.file} onLoadSuccess={this.handleDocumentLoaded}>
                    <div className="pdf-document-viewer-sidebar">
                        <Outline onItemClick={p => this.navigateToPage(p.pageNumber)} />
                    </div>
                    <div
                        className="pdf-document-viewer"
                        ref={el => {
                            if (el && !this.scrollContainerRef) {
                                this.scrollContainerRef = el;
                                el.addEventListener('scroll', this.handleScroll);
                            }
                        }}
                    >
                        <AutoSizer style={{ height: '100%', width: '100%', textAlign: 'center' }}>
                            {({ width }) =>
                                _.range(1, this.state.numPages + 1).map(v => (
                                    <Observer key={v}>
                                        {() => (
                                            <Page
                                                key={v}
                                                width={width * store!.scale * 0.8}
                                                renderTextLayer={false}
                                                ref={r => {
                                                    if (r) {
                                                        this.pageRefs[v] = r;
                                                    }
                                                }}
                                                pageNumber={v}
                                                renderAnnotations={false}
                                            />
                                        )}
                                    </Observer>
                                ))
                            }
                        </AutoSizer>
                    </div>
                </Document>
            </div>
        );
    }

    private handleDocumentLoaded = ({ numPages }: Pdf) => this.setState((s: State) => setPages(numPages, s));
}

export default observer(PdfViewer);
